import React from "react";
import { Trash2 } from "lucide-react";
import LicenseForm from "../../../components/forms/LicenseGenerationForm";

const PAGE_SIZE = 10;

const buildQueryString = (params) => {
	const q = new URLSearchParams();
	Object.entries(params).forEach(([k, v]) => {
		if (v !== undefined && v !== null && v !== "") q.append(k, v);
	});
	return q.toString();
};

const getDerivedStatus = (license) => {
	if (license.expiresAt && new Date(license.expiresAt) <= new Date()) return "EXPIRED";
	return "ACTIVE";
};

export const createCompassProductHandler = ({
	callApi,
	API_CONFIG,
	showError,
	showSuccess,
	getReadableErrorMessage,
}) => {
	const DEFAULT_FILTERS = {
		search: "",
		licenseFor: "all",
		licenseType: "all",
		status: "all",
		page: 1,
		limit: PAGE_SIZE,
	};

	const filterDefinitions = [
		{
			key: "search",
			label: "Search",
			type: "search",
			placeholder: "Search by license ID or fingerprint",
		},
		{
			key: "licenseFor",
			label: "License For",
			type: "select",
			options: [
				{ value: "all", label: "All" },
				{ value: "ran", label: "RAN" },
				{ value: "compass", label: "Compass" },
			],
		},
		{
			key: "licenseType",
			label: "License Type",
			type: "select",
			options: [
				{ value: "all", label: "All" },
				{ value: "TRIAL", label: "Trial" },
				{ value: "PERPETUAL", label: "Perpetual" },
			],
		},
		{
			key: "status",
			label: "Status",
			type: "select",
			options: [
				{ value: "all", label: "All" },
				{ value: "ACTIVE", label: "Active" },
				{ value: "EXPIRED", label: "Expired" },
			],
		},
	];

	const columns = ({ onDownload, onDelete }) => [
		{
			key: "licenseId",
			label: "License ID",
			render: (row) => row.licenseId || "—",
		},
		{
			key: "client",
			label: "Client",
			render: (row) => row.clientId?.clientName || "—",
		},
		{
			key: "partner",
			label: "Partner",
			render: (row) => row.partnerId?.partnerName || "—",
		},
		{
			key: "licenseFor",
			label: "License For",
			render: (row) => row.licenseFor?.toUpperCase() || "—",
		},
		{
			key: "licenseType",
			label: "Type",
			render: (row) => row.licenseType || "—",
		},
		{
			key: "status",
			label: "Status",
			render: (row) => {
				const status = getDerivedStatus(row);
				return (
					<span
						className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
							status === "ACTIVE"
								? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
								: "bg-rose-500/10 text-rose-500 border border-rose-500/20"
						}`}
					>
						{status}
					</span>
				);
			},
		},
		{
			key: "expiresAt",
			label: "Expiry",
			render: (row) => (row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : "Never"),
		},
		{
			key: "actions",
			label: "Actions",
			render: (row) => (
				<div className="flex items-center gap-2">
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (onDownload) onDownload(row.licenseId);
						}}
						disabled={getDerivedStatus(row) !== "ACTIVE"}
						className={`text-sm px-3 py-1 rounded-lg font-medium transition ${
							getDerivedStatus(row) !== "ACTIVE"
								? "bg-slate-200 text-slate-500 cursor-not-allowed"
								: "bg-brand text-white hover:bg-brand-dark"
						}`}
					>
						Download
					</button>

					{/* Enabled for expired and superseded rows too — those are
					    exactly the ones worth clearing out. */}
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (onDelete) onDelete(row);
						}}
						title="Delete license"
						className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-lg font-medium transition hover:opacity-80"
						style={{ color: "var(--danger)", border: "1px solid var(--danger)" }}
					>
						<Trash2 size={14} />
					</button>
				</div>
			),
		},
	];

	const prepareForm = async () => {
		try {
			const [clientsRes, partnersRes, ransRes] = await Promise.all([
				callApi(API_CONFIG.CLIENT.GET.METHOD, API_CONFIG.CLIENT.GET.ENDPOINT + "?isActive=true"),
				callApi(API_CONFIG.PARTNER.GET.METHOD, API_CONFIG.PARTNER.GET.ENDPOINT + "?isActive=true"),
				callApi(API_CONFIG.RAN.GET.METHOD, API_CONFIG.RAN.GET.ENDPOINT + "?isActive=true"),
			]);

			return {
				ok: true,
				data: {
					clients: clientsRes.data.data || [],
					partners: partnersRes.data.data || [],
					rans: ransRes.data.data || [],
				},
			};
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false, error };
		}
	};

	const submitForm = async (payload) => {
		try {
			await callApi(
				API_CONFIG.LICENSE.CREATE.METHOD,
				API_CONFIG.LICENSE.CREATE.ENDPOINT,
				payload
			);
			showSuccess("License generated successfully");
			return { ok: true };
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false, error };
		}
	};

	const fetchList = async ({ search, licenseFor, licenseType, status, page = 1, limit = PAGE_SIZE }) => {
		try {
			const query = buildQueryString({
				page,
				limit,
				search: search || undefined,
				licenseFor: licenseFor && licenseFor !== "all" ? licenseFor : undefined,
				licenseType: licenseType && licenseType !== "all" ? licenseType : undefined,
				status: status && status !== "all" ? status : undefined,
			});

			const res = await callApi(
				API_CONFIG.LICENSE.LIST.METHOD,
				`${API_CONFIG.LICENSE.LIST.ENDPOINT}?${query}`
			);

			return {
				rows: res.data.data || [],
				total: res.data.total || 0,
			};
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { rows: [], total: 0, error };
		}
	};

	const renderForm = ({ onSubmit, isLoading, formData }) => (
		<LicenseForm
			onSubmit={onSubmit}
			isLoading={isLoading}
			clients={formData?.clients || []}
			partners={formData?.partners || []}
			rans={formData?.rans || []}
		/>
	);

	return {
		prepareForm,
		submitForm,
		renderForm,
		fetchList,
		defaultFilters: DEFAULT_FILTERS,
		filterDefinitions,
		tableColumns: columns,
	};
};
